import styles from './TestimonialCarousel.module.scss';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Colors } from '../../../theme';
import { debounce } from '../../../utils/debounce';
import { Avatar } from '../../display/Avatar';
import { ScrollSnap } from '../../display/Carousel';
import { OnPaginateData, Paginator } from '../../navigation/Paginator';
import { TestimonialSoloProps } from '../TestimonialSolo';
import {
  BackgroundColors,
  remapDeprecatedColors,
} from '../TestimonialSolo/TestimonialMapping';
import { LegacyColorMap } from '@/theme/LegacyColorMap';

type TestimponialProps = TestimonialSoloProps & {
  mediaElement: React.ReactNode;
};

export type Props = {
  /**
   * Array of testimonial elements.
   * Fields:
   *
   * `quote`: Text content displayed on the top or below the avatar.
   *
   * `author`: Text content displayed below quote.
   *
   * `company`: Text content displayed below author initials.
   *
   * `hasAuthorAvatar`?: Whether or not Testimonial has author avatar. Default value is `False`.
   *
   * `avatarUrl`?: Author avatar url.
   *
   * `mediaElement`: an Image or a Video element that will be shown besides the Testimonial
   */
  testimonials: Array<Omit<TestimponialProps, 'backgroundColor'>>;
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava10' | 'ocean10' | 'sun10' | 'white' | 'cream' | 'neutral100' | 'acidWash' | 'neutral200'.
   */
  backgroundColor?: BackgroundColors;
};

/**
 * `TestimonialCarousel` can be used to show the testimonials in form of a carousel. You also get an option to show some video / image along with the testimonial.
 */
export const TestimonialCarousel = (props: Props) => {
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const testimonialContainerRef = useRef<HTMLDivElement>(null);
  const [testimonialContainerWidth, setTestimonialContainerWidth] = useState(0);

  useEffect(() => {
    // set initial width of the Testimonial Container
    setTestimonialContainerWidth(
      testimonialContainerRef?.current?.offsetWidth || 0
    );
  }, []);

  useLayoutEffect(() => {
    const onResize = debounce(() => {
      setTestimonialContainerWidth(
        testimonialContainerRef?.current?.offsetWidth || 0
      );
    }, 100);
    window.addEventListener('resize', onResize, { capture: true });
    return () => {
      window.removeEventListener('resize', onResize, { capture: true });
    };
  });

  const paginator = (
    <Paginator
      currentPageIndex={activeTestimonialIndex}
      onPaginate={({ pageIndex: index }: OnPaginateData) =>
        setActiveTestimonialIndex(index)
      }
      pageUrls={Array.from({ length: props.testimonials.length })}
    />
  );

  return (
    <div
      className={styles.testimonialCarouselContainer}
      style={{
        '--_zds-testimonialcarousel-background-color':
          Colors[
            LegacyColorMap[
              remapDeprecatedColors(props.backgroundColor || 'neutral100')
            ]
          ],
      }}
      data-zds
    >
      <div
        className={styles.testimonialContainerWrapper}
        ref={testimonialContainerRef}
        data-zds
      >
        {testimonialContainerWidth && (
          <ScrollSnap
            gap={60}
            itemWidth={testimonialContainerWidth}
            onScroll={(index: number) => {
              setActiveTestimonialIndex(index);
            }}
            pageIndex={activeTestimonialIndex}
            scrollSnapAlign="center"
          >
            {props.testimonials.map((testimonial, i) => (
              <div key={i} className={styles.testimonialContainer} data-zds>
                <div data-zds>{testimonial.mediaElement}</div>
                <div className={styles.testimonial} data-zds>
                  {testimonial.hasAuthorAvatar && (
                    <div className={styles.avatarContainer} data-zds>
                      <Avatar
                        data-testId="Avatar"
                        name={testimonial.author}
                        url={testimonial.avatarUrl}
                        size="medium"
                      />
                    </div>
                  )}
                  <div className={styles.quote} data-zds>
                    {testimonial.quote}
                  </div>
                  <p className={styles.author} data-zds>
                    {testimonial.author}, {testimonial.company}
                  </p>
                </div>
              </div>
            ))}
          </ScrollSnap>
        )}
      </div>
      {paginator}
    </div>
  );
};
